<script lang="ts">
  /**
   * Wabenflaggen: the flags you hold this week, who asked you for one, and what you asked
   * (amendment 13).
   *
   * An incoming request that is still open gets the three answers — hand it over, keep it,
   * report it. Everything else is history, labelled with what became of it. "Expired" is
   * worked out here from the week rather than stored: an unanswered request simply ran out
   * with its flag on Monday.
   */
  import { reasonOf, REQUEST_STATE_LABELS, requestState } from '$domain/flags.ts';
  import Avatar from './Avatar.svelte';
  import { flags } from './flags.svelte.ts';
  import { cachedPlace, reverseGeocode } from './place.ts';
  import { describeError } from './session.svelte.ts';
  import type { FlagRequestView } from './types.ts';

  type Props = { onclose: () => void; onselect: (cell: string) => void };
  const { onclose, onselect }: Props = $props();

  let busy = $state<string | undefined>();
  let error = $state('');
  let places = $state<Record<string, string>>({});

  const inbox = $derived(flags.inbox);
  const stateOf = (entry: FlagRequestView) =>
    requestState(entry.status, entry.week, inbox?.currentWeek ?? entry.week);

  $effect(() => {
    void flags.refresh();
  });

  /**
   * Names for the cells on screen, one Nominatim lookup a second.
   *
   * Their usage policy is an absolute maximum of one request per second, and an inbox with a
   * handful of unfamiliar places would otherwise fire them all at once. Cached names cost
   * nothing and are filled in immediately; only a real lookup waits its turn.
   */
  $effect(() => {
    const current = inbox;
    if (current === undefined) return;
    const cells = [
      ...new Set([
        ...current.held,
        ...current.incoming.map((entry) => entry.cell),
        ...current.sent.map((entry) => entry.cell),
      ]),
    ];
    let cancelled = false;
    void (async () => {
      let first = true;
      for (const cell of cells) {
        if (cancelled) return;
        const known = cachedPlace(cell);
        if (known !== undefined) {
          places = { ...places, [cell]: known };
          continue;
        }
        if (!first) await new Promise((resolve) => setTimeout(resolve, 1100));
        first = false;
        const name = await reverseGeocode(cell);
        if (name !== undefined && !cancelled) places = { ...places, [cell]: name };
      }
    })();
    return () => {
      cancelled = true;
    };
  });

  const placeOf = (cell: string) => places[cell] ?? 'eine Wabe';

  async function answer(entry: FlagRequestView, verb: 'accept' | 'decline' | 'report') {
    busy = entry.id;
    error = '';
    try {
      await flags.answer(entry.id, verb);
    } catch (cause) {
      error = describeError(cause);
      // The request may have moved on without us — somebody else was quicker. Show that.
      await flags.refresh();
    } finally {
      busy = undefined;
    }
  }
</script>

<section class="panel" aria-label="Flaggen">
  <header>
    <h2>🚩 Flaggen</h2>
    <button class="close" onclick={onclose} aria-label="Schließen">×</button>
  </header>

  <p class="hint">
    Die erste öffentliche Meldung der Woche in einer Wabe setzt ihre Flagge. Wer sie haben will,
    fragt — und wer sie hat, entscheidet. Montag geht alles von vorn los.
  </p>

  {#if error}<p class="hint error" role="alert">{error}</p>{/if}

  {#if inbox === undefined}
    <p class="hint">Einen Moment …</p>
  {:else}
    <div class="scroll">
      <h3>Deine Flaggen diese Woche</h3>
      {#if inbox.held.length === 0}
        <p class="hint">Noch keine. Melde als Erste in einer Wabe, dann gehört sie dir.</p>
      {:else}
        <ul class="held">
          {#each inbox.held as cell (cell)}
            <li><button onclick={() => onselect(cell)}>🚩 {placeOf(cell)}</button></li>
          {/each}
        </ul>
      {/if}

      <h3>Anfragen an dich</h3>
      {#if inbox.incoming.length === 0}
        <p class="hint">Niemand will gerade eine deiner Flaggen.</p>
      {:else}
        <ul class="requests">
          {#each inbox.incoming as entry (entry.id)}
            {@const reason = reasonOf(entry.reason)}
            {@const state = stateOf(entry)}
            <li class:open={state === 'open'}>
              <p class="who">
                <Avatar username={entry.other.username} emoji={entry.other.avatarEmoji} />
                @{entry.other.username}
                <small>für {placeOf(entry.cell)}</small>
              </p>
              <p class="reason">{reason.emoji} {reason.label}</p>
              {#if entry.note}<p class="note">„{entry.note}"</p>{/if}
              {#if state === 'open'}
                <div class="verbs">
                  <button disabled={busy !== undefined} onclick={() => answer(entry, 'accept')}>
                    Übergeben
                  </button>
                  <button
                    class="quiet"
                    disabled={busy !== undefined}
                    onclick={() => answer(entry, 'decline')}
                  >
                    Behalten
                  </button>
                  <button
                    class="quiet report"
                    disabled={busy !== undefined}
                    onclick={() => answer(entry, 'report')}
                    title="Die Anfrage verschwindet und ein Admin sieht sie sich an."
                  >
                    Melden
                  </button>
                </div>
              {:else}
                <p class="state">{REQUEST_STATE_LABELS[state]}</p>
              {/if}
            </li>
          {/each}
        </ul>
      {/if}

      <h3>Deine Anfragen</h3>
      {#if inbox.sent.length === 0}
        <p class="hint">Du hast noch um keine Flagge gebeten.</p>
      {:else}
        <ul class="requests">
          {#each inbox.sent as entry (entry.id)}
            {@const reason = reasonOf(entry.reason)}
            {@const state = stateOf(entry)}
            <li>
              <p class="who">
                an <Avatar username={entry.other.username} emoji={entry.other.avatarEmoji} />
                @{entry.other.username}
                <small>für {placeOf(entry.cell)}</small>
              </p>
              <p class="reason">{reason.emoji} {reason.label}</p>
              <p class="state state-{state}">{REQUEST_STATE_LABELS[state]}</p>
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  {/if}
</section>

<style>
  .panel {
    position: absolute;
    inset: max(3.5rem, env(safe-area-inset-top)) 1rem max(1rem, env(safe-area-inset-bottom));
    z-index: 4;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-width: 30rem;
    margin-inline: auto;
    padding: 0.75rem 1rem 1rem;
    border: 1px solid var(--border);
    border-radius: 1rem;
    background: var(--card);
    box-shadow: 0 8px 32px rgb(0 0 0 / 30%);
  }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  h2 {
    margin: 0;
    font-size: 1.1rem;
  }

  h3 {
    margin: 0.75rem 0 0.25rem;
    font-size: 0.875rem;
    color: var(--muted);
    font-weight: 600;
  }

  .close {
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 1.75rem;
    line-height: 1;
    cursor: pointer;
  }

  .scroll {
    flex: 1;
    overflow-y: auto;
  }

  .hint {
    margin: 0;
    font-size: 0.8125rem;
    color: var(--muted);
  }

  .error {
    color: var(--accent);
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .held button {
    width: 100%;
    padding: 0.45rem 0.6rem;
    border: 1px solid var(--border);
    border-radius: 0.6rem;
    background: var(--bg);
    color: var(--fg);
    font-size: 0.875rem;
    text-align: left;
    cursor: pointer;
  }

  .held li + li {
    margin-top: 0.3rem;
  }

  .requests li {
    padding: 0.55rem 0.2rem;
    border-top: 1px solid var(--border);
  }

  .requests li.open {
    border-left: 3px solid #ff4d4f;
    padding-left: 0.6rem;
  }

  .requests p {
    margin: 0;
  }

  .who {
    font-size: 0.9375rem;
  }

  .who small {
    color: var(--muted);
    margin-left: 0.25rem;
  }

  .reason {
    font-size: 0.875rem;
  }

  .note {
    margin-top: 0.15rem;
    font-size: 0.8125rem;
    font-style: italic;
    overflow-wrap: anywhere;
  }

  .state {
    margin-top: 0.2rem;
    font-size: 0.75rem;
    color: var(--muted);
  }

  .state-accepted {
    color: #2e9d5b;
  }

  .verbs {
    display: flex;
    gap: 0.4rem;
    margin-top: 0.45rem;
    flex-wrap: wrap;
  }

  .verbs button {
    padding: 0.35rem 0.7rem;
    border: 1px solid #ff4d4f;
    border-radius: 0.6rem;
    background: color-mix(in srgb, #ff4d4f 14%, var(--bg));
    color: var(--fg);
    font-size: 0.8125rem;
    cursor: pointer;
  }

  .verbs button.quiet {
    border-color: var(--border);
    background: var(--bg);
  }

  .verbs button.report {
    margin-left: auto;
    color: var(--muted);
  }
</style>
